// Gli estremi che i documenti legali del sito dichiarano, in un posto solo.
//
// Fino a oggi il titolare, la sede, la partita IVA, le caselle e il
// telefono erano scritti a mano in quattro punti: il piede,
// l'informativa privacy, le note legali e l'accordo di riservatezza.
// Sono tutti **segnaposto**, e
// vanno sostituiti con i dati veri della società prima della
// pubblicazione: finché restano questi, l'informativa indica un titolare
// che non esiste, che non è una formalità mancata ma il motivo per cui
// l'interessato non sa a chi scrivere per esercitare i suoi diritti.
// Sostituirli qui li cambia dappertutto.
//
// Il file non importa niente di server-only ed è letto anche dal modulo
// di /contatti, che è un componente client: non metterci mai una chiave
// o un indirizzo interno.

/** Ragione sociale del titolare del trattamento e gestore del sito. */
export const TITOLARE = "Osservatorio Italiano";

/** Sede legale, nelle due righe con cui la scrive il piede. */
export const SEDE = {
  via: "Via dei Pionieri 1",
  citta: "20121 Milano · IT",
  /** Riga unica, per i testi discorsivi degli articoli. */
  estesa: "Via dei Pionieri 1, 20121 Milano (IT)",
} as const;

/** Il numero come sta scritto oggi: sostituire **questo** con quello vero. */
const PARTITA_IVA_SCRITTA = "00000000000";

/** Undici zeri non sono una partita IVA che qualcuno possa verificare:
 *  dichiararla in pagina vuol dire affermare un dato falso, che è peggio
 *  che non dichiararlo. */
const PARTITA_IVA_SEGNAPOSTO = "00000000000";

/**
 * Partita IVA da rendere, oppure `null` finché è il segnaposto.
 *
 * Chi la scrive in pagina deve saltare anche l'etichetta «P.IVA» e il
 * separatore che la precede, non solo il numero: un «P.IVA » appeso e
 * vuoto si legge come un dato perso. Per i testi discorsivi c'è
 * `INCISO_PARTITA_IVA`, che dà l'inciso intero o niente.
 */
export const PARTITA_IVA: string | null =
  PARTITA_IVA_SCRITTA === PARTITA_IVA_SEGNAPOSTO ? null : PARTITA_IVA_SCRITTA;

/** «, P.IVA 01234567890» da attaccare dopo la sede, o stringa vuota. */
export const INCISO_PARTITA_IVA = PARTITA_IVA ? `, P.IVA ${PARTITA_IVA}` : "";

/** Casella ordinaria: è quella che i messaggi d'errore del modulo
 *  indicano a chi non riesce a inviare, quindi deve ricevere davvero. */
export const EMAIL_TITOLARE = "info@osservatorioitaliano.it";

/** Casella certificata, per le comunicazioni che richiedono data certa:
 *  l'esercizio dei diritti dell'interessato è una di quelle. */
export const PEC_TITOLARE = "pec@osservatorioitaliano.it";

/**
 * Recapito telefonico, nelle due forme che servono: quella che si legge
 * e quella che compone il link `tel:`.
 *
 * Sono scritte separatamente e non ricavate l'una dall'altra perché la
 * seconda non tollera niente di quello che rende leggibile la prima
 * (spazi, punti, il prefisso fra parentesi): una derivazione a colpi di
 * `replace` funziona finché qualcuno non cambia il formato scritto, e
 * allora smette di comporre il numero senza che si veda.
 */
export const TELEFONO = {
  esteso: "+39 02 0000 0000",
  /** Formato E.164, senza spazi: è quello che i telefoni compongono. */
  href: "tel:+390200000000",
} as const;

/**
 * Per quanto si tiene una richiesta dei moduli che non ha avuto seguito.
 * Lo dicono l'informativa (§ 7.1 di /privacy), il riquadro dei consensi
 * sotto i due moduli e il pannello «24 mesi» di /contatti: tre punti che
 * devono dire lo stesso numero, e che lo leggono da qui. `parole` è la
 * forma della prosa, `breve` quella della cifra grande.
 */
export const CONSERVAZIONE = {
  mesi: 24,
  parole: "ventiquattro mesi",
  breve: "24 mesi",
} as const;

/**
 * Versione dell'informativa privacy, in formato ISO.
 *
 * Non è decorazione: viaggia insieme a ogni richiesta del modulo di
 * /contatti ed è la sola cosa che, a distanza di anni, dice **quale**
 * testo qualcuno aveva davanti quando ha dichiarato di averlo letto.
 * Una prova di presa visione che non dice a che cosa si riferisce non
 * prova niente.
 *
 * Va alzata insieme a `INFORMATIVA_AGGIORNATA_AL` ogni volta che cambia
 * la sostanza dell'informativa: finalità nuove, destinatari nuovi, tempi
 * di conservazione diversi. Una correzione di refuso non la muove.
 */
export const INFORMATIVA_VERSIONE = "2026-09-22";

/** La stessa data, per esteso, come la legge chi apre la pagina. */
export const INFORMATIVA_AGGIORNATA_AL = "22 settembre 2026";

/** Ultima revisione delle note legali. Le due date sono separate perché
 *  i due testi cambiano per ragioni diverse e quasi mai insieme. */
export const NOTE_LEGALI_AGGIORNATE_AL = "16 settembre 2026";
